import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchCategories,
  fetchProducts,
  loadHomeProducts,
  setParams,
  fetchBrandsByCategory,
  fetchModelsByBrand,
} from "../store/features/productsSlice";
import useScrollMemory from "../hooks/useScrollMemory";

import FilterSortBar from "../components/FilterSortBar";
import ProductCard from "../components/ProductCard";
import BannerSlider from "../components/BannerSlider";
import { Chip, Loader } from "../components/ui";
import { toList } from "../utils/format";

// Рядок «пігулок»: «Все …» + варіанти
const ChipRow = ({ title, allLabel, items, selected, onSelect }) => (
  <section className="mt-5 px-2">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{title}</h3>
    <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
      <Chip active={!selected} onClick={() => onSelect(null)}>
        {allLabel}
      </Chip>
      {items.map((item) => (
        <Chip key={item.id} active={selected?.id === item.id} onClick={() => onSelect(item)}>
          {item.name}
        </Chip>
      ))}
    </div>
  </section>
);

const HomePage = () => {
  const dispatch = useDispatch();

  const { categories, brandsByCategory, modelsByBrand, products, next, status, params } =
    useSelector((state) => state.products);
  const searchTerm = useSelector((state) => state.search.term);

  const categoryList = toList(categories);
  const brandList = toList(brandsByCategory);
  const modelList = toList(modelsByBrand);

  // Вибране береться з фільтрів у Redux — тож після повернення зі сторінки товару
  // «пігулки» показують ті самі фільтри, за якими відфільтровано список
  const selectedCategory = categoryList.find((c) => c.slug === params.category) || null;
  const selectedBrand = brandList.find((b) => b.slug === params.brand) || null;
  const selectedModel = modelList.find((m) => m.slug === params.model) || null;

  // Повернення зі сторінки товару — на те саме місце каталогу
  useScrollMemory("home", products.length > 0);

  // Оновити фільтри і завантажити каталог (лише якщо фільтри справді змінились)
  const reload = (changes) => {
    if (changes) dispatch(setParams(changes));
    dispatch(loadHomeProducts());
  };

  useEffect(() => {
    if (!categoryList.length) dispatch(fetchCategories());
  }, [dispatch, categoryList.length]);

  // Пошук; спрацьовує й при першому відкритті — це і є початкове завантаження товарів.
  // Якщо каталог для цих фільтрів уже є (повернулись з товару) — нічого не вантажимо.
  useEffect(() => {
    dispatch(setParams({ search: searchTerm || null }));
    dispatch(loadHomeProducts());
  }, [dispatch, searchTerm]);

  // Нескінченна прокрутка
  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 300 &&
        status !== "loading" &&
        next
      ) {
        dispatch(fetchProducts({ url: next }));
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [dispatch, next, status]);

  const selectCategory = (category) => {
    if (category) dispatch(fetchBrandsByCategory(category.slug));
    reload({ category: category?.slug || null, brand: null, model: null });
  };

  const selectBrand = (brand) => {
    if (brand) dispatch(fetchModelsByBrand(brand.slug));
    reload({ brand: brand?.slug || null, model: null });
  };

  const selectModel = (model) => {
    reload({ model: model?.slug || null });
  };

  return (
    <div className="text-white min-h-screen">
      <BannerSlider />

      {/* CATEGORIES */}
      <section className="mt-6 px-2">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Категории</h2>
          <Chip active={!selectedCategory} onClick={() => selectCategory(null)} className="text-xs">
            Все
          </Chip>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
          {categoryList.map((category) => {
            const active = selectedCategory?.id === category.id;
            return (
              <button
                key={category.id}
                onClick={() => selectCategory(category)}
                className="flex-shrink-0 w-24 group"
              >
                <div
                  className={`h-24 rounded-2xl overflow-hidden bg-slate-800 border-2 transition ${
                    active ? "border-primary shadow-lg shadow-primary/30" : "border-slate-700"
                  }`}
                >
                  {category.image && (
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                  )}
                </div>
                <p
                  className={`text-center text-xs font-semibold mt-1.5 leading-tight ${
                    active ? "text-blue-300" : "text-slate-200"
                  }`}
                >
                  {category.name}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      {selectedCategory && brandList.length > 0 && (
        <ChipRow
          title="Бренды"
          allLabel="Все бренды"
          items={brandList}
          selected={selectedBrand}
          onSelect={selectBrand}
        />
      )}

      {selectedBrand && modelList.length > 0 && (
        <ChipRow
          title="Модели"
          allLabel="Все модели"
          items={modelList}
          selected={selectedModel}
          onSelect={selectModel}
        />
      )}

      <section className="mt-6 px-2">
        <FilterSortBar onApply={() => reload()} />
      </section>

      <section className="mt-6 px-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {status === "loading" && <Loader />}
    </div>
  );
};

export default HomePage;
